/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/member-ordering */
import {
    AfterViewInit,
    Component,
    forwardRef,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    inject,
    input,
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    UntypedFormControl,
    NgControl,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    FormsModule,
    ReactiveFormsModule,
} from '@angular/forms';
import {
    MatDatepicker,
    MatDatepickerInputEvent,
    MatDatepickerToggle,
    MatDatepickerInput,
} from '@angular/material/datepicker';

import moment from 'moment';
import { Subject } from 'rxjs';
import { ConfigurationHandlerService } from '../../core/configuration-handler.service';
import { ErrorsTranslatePipe } from '../../shared/pipes/errors-translate.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';
import { AriaCalendarDirective } from '../../shared/directives/a11y/aria-calendar.directive';
import { MatInput, MatInputModule } from '@angular/material/input';
import {
    MatFormField,
    MatLabel,
    MatSuffix,
    MatError,
} from '@angular/material/form-field';
import { CustomTranslatePipe } from '../../shared/pipes/custom-translate.pipe';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

const dateInputFormats = [
    'YYYY',
    'YYYYMMDD',
    'YYYYMM',
    'MMM YYYY',
    'MMMM YYYY',
    'MM/YYYY',
    'MM.YYYY',
    'YYYY-MM',
    'YYYY-MM-DD',
    'DD/MM/YYYY',
    'MM/YYYY',
    'MM/DD/YYYY',
    'L',
    'LL',
];

@Component({
    selector: 'esp-datepicker',
    templateUrl: './esp-datepicker.component.html',
    styleUrls: ['./esp-datepicker.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => EspDatepickerComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => EspDatepickerComponent),
            multi: true,
        },
    ],
    imports: [
        MatInputModule,
        FormsModule,
        ReactiveFormsModule,
        MatDatepickerToggle,
        MatDatepicker,
        AriaCalendarDirective,
        MatDatepickerInput,
        DatePipe,
        TranslateModule,
        ErrorsTranslatePipe,
        CustomTranslatePipe,
        MatIcon,
        MatTooltip,
    ],
})
export class EspDatepickerComponent
    implements ControlValueAccessor, OnInit, AfterViewInit, OnDestroy
{
    private dateFormatService = inject(ConfigurationHandlerService);
    injector = inject(Injector);

    readonly view = input<'month' | 'year' | 'multi-year'>('month');
    readonly partialType = input<'year' | 'month' | null>(null);
    readonly isPartial = input<boolean>(undefined);
    readonly label = input<string>(undefined);
    readonly hint = input<string>(undefined);
    readonly translatedLabel = input<string>(undefined);
    readonly placeholder = input<string>(undefined);
    readonly autoPopulated = input(false);
    readonly serverDateFormat = input('YYYYMMDD');
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    @Input() date: Date;
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    @Input() dateFormat = 'dd/MM/y';
    // TODO: Skipped for migration because:
    //  Accessor inputs cannot be migrated as they are too complex.
    @Input()
    get minDate(): any {
        return this._minDate;
    }
    set minDate(date: any) {
        this._minDate = moment(date, dateInputFormats);
    }
    private _minDate: any;

    // TODO: Skipped for migration because:
    //  Accessor inputs cannot be migrated as they are too complex.
    @Input()
    get maxDate(): any {
        return this._maxDate;
    }
    set maxDate(date: any) {
        this._maxDate = moment(date, dateInputFormats);
    }
    private _maxDate: any;

    onChange = (_: any) => {};
    onTouched = () => {};

    _dateFormat = 'dd/MM/y';
    mode = 'day';

    dateCtlPicker = new UntypedFormControl('');
    control: UntypedFormControl = new UntypedFormControl('');

    private destroy = new Subject<void>();
    private readonly YEAR = 'year';
    private readonly MONTH = 'month';
    private readonly DAY = 'day';

    ngOnInit(): void {
        this.dateFormat = this.dateFormatService.systemDateFormat();
    }

    ngAfterViewInit(): void {
        const ngControl: NgControl = this.injector.get(NgControl, null);
        if (ngControl) {
            setTimeout(() => {
                this.control = ngControl.control as UntypedFormControl;
            });
        }
    }

    writeValue(value): void {
        this.date = value;
        if (value) this.initialDateMode(value);
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    private initialDateMode(data: string) {
        let format = this.isPartial() ? 'YYYYMMDD' : this.dateFormat;
        if (this.isPartial()) {
            if (data?.length === 4) {
                this.mode = this.YEAR;
                format = format.substring(0, 4);
            } else if (data?.length === 6) {
                this.mode = this.MONTH;
                format = format.substring(0, 6);
            } else {
                this.mode = this.DAY;
            }
        }

        this.setDateFormat();
        const _date = moment(data, format).toDate();
        this.date = _date;
        this.dateCtlPicker.setValue(_date);
    }

    private convertDateToPartial(_date: Date) {
        let partialDate;
        switch (this.mode) {
            case this.DAY: {
                partialDate = moment(_date).format(this.serverDateFormat());
                break;
            }
            case this.MONTH: {
                partialDate = moment(_date).format('YYYYMM');
                break;
            }
            case this.YEAR: {
                partialDate = moment(_date).format('YYYY');
                break;
            }
        }
        return partialDate;
    }

    private setDateFormat() {
        switch (this.mode) {
            case this.DAY: {
                this._dateFormat = this.dateFormat;
                break;
            }
            case this.MONTH: {
                this._dateFormat = this.dateFormat.replace(
                    /(^(d|D)+[^mMyY])|([^mMyYdD](d|D)+)/,
                    '',
                );
                break;
            }
            case this.YEAR: {
                const match = this.dateFormat.match(/(y|Y)+/);
                this._dateFormat = match && match.length > 0 ? match[0] : null;
                break;
            }
            default: {
                this._dateFormat = this.dateFormat;
                break;
            }
        }

        if (!this._dateFormat) {
            this._dateFormat = this.dateFormat;
        }
    }

    dateChanged(data: MatDatepickerInputEvent<Date>) {
        this.mode = this.DAY;
        this.setDate(data.value);
    }

    chosenMonthHandler(data: Date) {
        if (this.isPartial()) {
            this.mode = this.MONTH;
        }
        this.setDate(data);
    }

    chosenYearHandler(data: Date, picker: MatDatepicker<any>) {
        if (this.isPartial()) {
            this.mode = this.YEAR;
        }
        this.setDate(data);
        if (this.partialType() === 'year') {
            picker.close();
        }
    }

    dateInputChangeHandler(event) {
        const input = event.target.value;
        const newDate = this.isPartial()
            ? this.getModeDateByInput(input)
            : input;
        this.setDate(newDate);
        this.dateCtlPicker.setValue(newDate);
        this.control.updateValueAndValidity();
    }

    setDate(data: Date) {
        if (!data) {
            this.onChange('');
            return;
        }
        this.date = data;
        if (this.isPartial()) {
            this.setDateFormat();
            const result = this.convertDateToPartial(this.date);
            this.onChange(result);
            this.dateCtlPicker.setValue(result);
        } else {
            this.onChange(this.date);
            this.dateCtlPicker.setValue(this.date);
        }
    }

    getModeDateByInput(input) {
        let _date = moment.parseZone(input, 'YYYY', true);
        if (_date.isValid()) {
            this.mode = this.YEAR;
            return _date.toDate();
        }
        _date = moment.parseZone(input, 'MM/YYYY', true);
        if (_date.isValid()) {
            this.mode = this.MONTH;
            return _date.toDate();
        }
        _date = moment.parseZone(input, dateInputFormats, true);
        if (_date.isValid()) {
            this.mode = this.DAY;
            return _date.toDate();
        }
    }

    validate(control: AbstractControl): ValidationErrors | null {
        if (!control.value) return null;
        const dateValue = control.value;
        const err = {
            dateFormatInvalid: true,
        };
        // const datepickerErrors = this.dateCtlPicker.errors;
        const d = moment(dateValue, dateInputFormats);
        return !d.isValid() ? { ...control.errors, err } : null;
    }

    ngOnDestroy(): void {
        this.destroy.next();
        this.destroy.complete();
    }
}
